<template>
  <SelectPortal>
    <SelectContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn(selectContentVariant({ position }), props.class)"
    >
      <SelectScrollUpButton />
      <SelectViewport
        :class="cn(selectViewportVariant({ position, size: variants.size }))"
      >
        <slot />
      </SelectViewport>
      <SelectScrollDownButton />
    </SelectContent>
  </SelectPortal>
</template>

<script setup>
import { computed, inject } from 'vue';
import {
  SelectContent,
  SelectPortal,
  SelectViewport,
  useForwardPropsEmits,
} from 'radix-vue';
import { cva } from 'class-variance-authority';
import { SelectScrollDownButton } from './SelectScrollDownButton.vue';
import { SelectScrollUpButton } from './SelectScrollUpButton.vue';
import { cn } from '../../utils.js';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  align: {
    type: String,
    default: 'start',
  },
  alignOffset: {
    type: Number,
    default: 0,
  },
  arrowPadding: {
    type: Number,
    default: 0,
  },
  as: {
    type: String,
    default: 'div',
  },
  asChild: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: 'popper',
  },
  forceMount: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits([
  'closeAutoFocus',
  'escapeKeyDown',
  'SelectContentEmits',
]);

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props;

  return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const variants = inject('select-variants');

const selectContentVariant = cva(
  [
    'relative z-50 max-h-96 min-w-32 overflow-hidden rounded border-neutral-900 bg-neutral-900',
    'text-neutral-400 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
    'data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
    'max-w-[--radix-popper-anchor-width]',
  ],
  {
    variants: {
      position: {
        popper:
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
      },
    },
    defaultVariant: {
      position: 'popper',
    },
  }
);

const selectViewportVariant = cva([], {
  variants: {
    position: {
      popper:
        'h-[--radix-select-trigger-height] w-full min-w-[--radix-select-trigger-width]',
    },
    size: {
      sm: 'p-1',
      lg: 'p-1',
      xl: 'p-2',
    },
  },
  defaultVariants: {
    position: 'popper',
    size: 'lg',
  },
});
</script>
